import { Button } from "@/components/ui/button";
import { getProjectQueryOptions } from "@/lib/hooks/useProjects";
import type { McpSessionRecord } from "@/stores/mcpSessionStore";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { Deployment } from "../../../../schemas";

import { ProjectStatusSection } from "./ProjectStatusSection";

interface ProjectHeaderProps {
  projectId: string;
  session?: McpSessionRecord;
}

export function ProjectHeader({ projectId, session }: ProjectHeaderProps) {
  const { data: projectData, isLoading } = useQuery(
    getProjectQueryOptions(projectId),
  );

  const project = projectData?.project;

  if (isLoading) {
    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="h-8 w-48 bg-muted animate-pulse rounded" />
          <div className="h-5 w-32 bg-muted animate-pulse rounded" />
        </div>
        <div className="flex items-center gap-2">
          <div className="h-9 w-20 bg-muted animate-pulse rounded" />
          <div className="h-9 w-24 bg-muted animate-pulse rounded" />
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-semibold text-destructive">
            Project Not Found
          </h1>
          <p className="text-sm text-muted-foreground">
            Project {projectId} could not be loaded
          </p>
        </div>
      </div>
    );
  }

  const displayName = project.appName || "Untitled Project";
  const deployment = project.latestDeployment ?? null;
  const deploymentUrl = getDeploymentUrl(deployment, displayName);

  return (
    <div className="py-2">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              {displayName}
            </h1>
          </div>
          {/* <p className="text-sm text-muted-foreground">
            Monitor deployments, MCP connectivity, and telemetry at a glance.
          </p> */}
        </div>

        <Button variant="outline" size="sm" asChild>
          <Link to="/projects">&laquo; Back to Projects</Link>
        </Button>
      </div>

      <ProjectStatusSection
        projectId={projectId}
        deploymentUrl={deploymentUrl}
        deployment={deployment}
        session={session}
      />
    </div>
  );
}

function getDeploymentUrl(deployment: Deployment | null, displayName: string) {
  if (!deployment || deployment.status !== "complete") {
    return undefined;
  }

  return deployment.url ?? `https://${displayName}.fp.dev`;
}
